import { SubscriberPaymentStepErrorSummary } from 'src/app/admin-api';
import {
  BillingCycleGatewayReport,
  BillingCycleGatewayReportRow,
  BillingCycleLoteItem,
  BillingCycleWithoutGatewayAttempt
} from 'src/app/services/billing-cycle.service';

/**
 * Cada cobrança é um assinante dentro de um lote executado. O mesmo assinante
 * aparece novamente se voltar em outra tentativa, por isso os totais são de
 * cobranças e não de assinantes distintos.
 */
export interface BillingMonthResultTotals {
  approved: number;
  refused: number;
  inProgress: number;
  /** Cobranças com desfecho: aprovadas + recusadas. Base da conversão. */
  settled: number;
  total: number;
  conversionPercent: number | null;
}

export interface BillingMonthAttemptResult extends BillingMonthResultTotals {
  attempt: number;
  rounds: number;
  /** Diferença em pontos percentuais contra a primeira tentativa do mês. */
  marginVsFirstAttempt: number | null;
}

export interface BillingMonthTimelinePoint extends BillingMonthResultTotals {
  executedAt: Date;
  label: string;
}

export interface BillingMonthRoundOption {
  paymentRoundId: number;
  label: string;
  refused: number;
}

export interface BillingMonthRefusalReason {
  reason: string;
  steps: string | null;
  total: number;
  sharePercent: number | null;
}

export interface BillingMonthReport {
  totals: BillingMonthResultTotals;
  attempts: BillingMonthAttemptResult[];
  timeline: BillingMonthTimelinePoint[];
  roundsWithRefusal: BillingMonthRoundOption[];
}

const UNKNOWN_REFUSAL_REASON = 'Sem mensagem registrada';

export function calculateConversionPercent(
  approved: number,
  refused: number
): number | null {
  const settled = approved + refused;
  if (settled <= 0) {
    return null;
  }
  return roundToOneDecimal((approved / settled) * 100);
}

export function calculateMarginInPercentagePoints(
  conversionPercent: number | null,
  baselinePercent: number | null
): number | null {
  if (conversionPercent === null || baselinePercent === null) {
    return null;
  }
  return roundToOneDecimal(conversionPercent - baselinePercent);
}

export function buildBillingMonthReport(
  rounds: Array<BillingCycleLoteItem> | null | undefined
): BillingMonthReport {
  const validRounds = (rounds ?? []).filter((round) => !!round);

  return {
    totals: buildMonthTotals(validRounds),
    attempts: buildAttemptResults(validRounds),
    timeline: buildTimeline(validRounds),
    roundsWithRefusal: buildRoundsWithRefusal(validRounds)
  };
}

export function buildRefusalReasons(
  summaries: Array<SubscriberPaymentStepErrorSummary> | null | undefined,
  limit = 10
): Array<BillingMonthRefusalReason> {
  const grouped = new Map<string, { steps: Set<string>; total: number }>();

  for (const summary of summaries ?? []) {
    if (!summary) {
      continue;
    }
    const reason = summary.error?.trim() || UNKNOWN_REFUSAL_REASON;
    const current = grouped.get(reason) ?? { steps: new Set<string>(), total: 0 };
    current.total += toCount(summary.total);
    if (summary.step) {
      current.steps.add(summary.step);
    }
    grouped.set(reason, current);
  }

  const overallTotal = Array.from(grouped.values()).reduce(
    (sum, item) => sum + item.total,
    0
  );

  return Array.from(grouped.entries())
    .map(([reason, item]) => ({
      reason,
      steps: item.steps.size ? Array.from(item.steps).sort().join(', ') : null,
      total: item.total,
      sharePercent:
        overallTotal > 0
          ? roundToOneDecimal((item.total / overallTotal) * 100)
          : null
    }))
    .sort((first, second) => second.total - first.total)
    .slice(0, Math.max(0, limit));
}

function buildMonthTotals(
  rounds: Array<BillingCycleLoteItem>
): BillingMonthResultTotals {
  let approved = 0;
  let refused = 0;
  let inProgress = 0;

  for (const round of rounds) {
    approved += countApproved(round);
    refused += countRefused(round);
    inProgress += countInProgress(round);
  }

  return buildTotals(approved, refused, inProgress);
}

function buildAttemptResults(
  rounds: Array<BillingCycleLoteItem>
): Array<BillingMonthAttemptResult> {
  const grouped = new Map<
    number,
    { approved: number; refused: number; inProgress: number; rounds: number }
  >();

  for (const round of rounds) {
    const attempt = resolveAttempt(round);
    const current =
      grouped.get(attempt) ??
      { approved: 0, refused: 0, inProgress: 0, rounds: 0 };
    current.approved += countApproved(round);
    current.refused += countRefused(round);
    current.inProgress += countInProgress(round);
    current.rounds += 1;
    grouped.set(attempt, current);
  }

  const ordered = Array.from(grouped.entries()).sort(
    (first, second) => first[0] - second[0]
  );
  const baselinePercent = ordered.length
    ? calculateConversionPercent(ordered[0][1].approved, ordered[0][1].refused)
    : null;

  return ordered.map(([attempt, counters], index) => {
    const totals = buildTotals(
      counters.approved,
      counters.refused,
      counters.inProgress
    );
    return {
      ...totals,
      attempt,
      rounds: counters.rounds,
      marginVsFirstAttempt:
        index === 0
          ? null
          : calculateMarginInPercentagePoints(
              totals.conversionPercent,
              baselinePercent
            )
    };
  });
}

function buildTimeline(
  rounds: Array<BillingCycleLoteItem>
): Array<BillingMonthTimelinePoint> {
  const executedRounds = rounds
    .map((round) => ({ round, executedAt: parseDate(round.dateExecuted) }))
    .filter((item) => !!item.executedAt)
    .sort(
      (first, second) =>
        first.executedAt.getTime() - second.executedAt.getTime()
    );

  let approved = 0;
  let refused = 0;
  let inProgress = 0;

  return executedRounds.map(({ round, executedAt }) => {
    approved += countApproved(round);
    refused += countRefused(round);
    inProgress += countInProgress(round);
    return {
      ...buildTotals(approved, refused, inProgress),
      executedAt,
      label: formatTimelineLabel(executedAt)
    };
  });
}

function buildRoundsWithRefusal(
  rounds: Array<BillingCycleLoteItem>
): Array<BillingMonthRoundOption> {
  return rounds
    .filter(
      (round) => round.paymentRoundId != null && countRefused(round) > 0
    )
    .map((round) => {
      const refused = countRefused(round);
      const executedAt = parseDate(round.dateExecuted);
      const when = executedAt ? ` · ${formatTimelineLabel(executedAt)}` : '';
      return {
        paymentRoundId: round.paymentRoundId,
        refused,
        label: `Lote ${round.paymentRoundId}${when} · ${refused} recusadas`
      };
    })
    .sort((first, second) => second.paymentRoundId - first.paymentRoundId);
}

function buildTotals(
  approved: number,
  refused: number,
  inProgress: number
): BillingMonthResultTotals {
  const settled = approved + refused;
  return {
    approved,
    refused,
    inProgress,
    settled,
    total: settled + inProgress,
    conversionPercent: calculateConversionPercent(approved, refused)
  };
}

function countApproved(round: BillingCycleLoteItem): number {
  return (
    toCount(round.paidCount) +
    toCount(round.activationCount) +
    toCount(round.subscriberEditionCount) +
    toCount(round.finishedCount)
  );
}

function countRefused(round: BillingCycleLoteItem): number {
  return toCount(round.failedCount);
}

function countInProgress(round: BillingCycleLoteItem): number {
  return toCount(round.validationCount) + toCount(round.paymentCount);
}

function resolveAttempt(round: BillingCycleLoteItem): number {
  const attempt = Number(round.maxAttempt ?? 1);
  if (!Number.isFinite(attempt) || attempt < 1) {
    return 1;
  }
  return Math.trunc(attempt);
}

function toCount(value: number | null | undefined): number {
  const parsed = Number(value ?? 0);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0;
  }
  return parsed;
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatTimelineLabel(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month} ${hours}:${minutes}`;
}

function roundToOneDecimal(value: number): number {
  return Math.round(value * 10) / 10;
}

const GATEWAY_EMPTY_MARK = '—';
const WITHOUT_GATEWAY_LABEL = 'Sem tentativa no gateway';

export interface BillingMonthGatewayDisplayRow {
  gatewayName: string;
  distinctSubscribers: string;
  paid: string;
  refused: string;
  inFlight: string;
  approvalRatePercent: string;
  sharePercent: string;
}

export interface BillingMonthGatewaySectionView {
  rows: BillingMonthGatewayDisplayRow[];
  withoutGateway: BillingMonthGatewayDisplayRow | null;
  isEmpty: boolean;
}

export function emptyGatewayApprovalSection(): BillingMonthGatewaySectionView {
  return { rows: [], withoutGateway: null, isEmpty: false };
}

export function buildGatewayApprovalSection(
  report: BillingCycleGatewayReport | null | undefined
): BillingMonthGatewaySectionView {
  if (!report) {
    return emptyGatewayApprovalSection();
  }

  const gateways = (report.gateways ?? []).filter((row) => !!row);
  return {
    rows: gateways.map((row) => toGatewayDisplayRow(row, GATEWAY_EMPTY_MARK)),
    withoutGateway: report.withoutGatewayAttempt
      ? toResidualDisplayRow(report.withoutGatewayAttempt)
      : null,
    isEmpty: gateways.length === 0
  };
}

export function formatGatewayCount(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return GATEWAY_EMPTY_MARK;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return GATEWAY_EMPTY_MARK;
  }
  return new Intl.NumberFormat('pt-BR').format(parsed);
}

export function formatGatewayPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return GATEWAY_EMPTY_MARK;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return GATEWAY_EMPTY_MARK;
  }
  return `${parsed.toFixed(1).replace('.', ',')}%`;
}

function toResidualDisplayRow(
  row: BillingCycleWithoutGatewayAttempt
): BillingMonthGatewayDisplayRow {
  return toGatewayDisplayRow(
    {
      gatewayName: row.label,
      distinctSubscribers: row.distinctSubscribers,
      paid: row.paid,
      refused: row.refused,
      inFlight: row.inFlight,
      approvalRatePercent: row.approvalRatePercent,
      sharePercent: row.sharePercent
    },
    WITHOUT_GATEWAY_LABEL
  );
}

function toGatewayDisplayRow(
  row: BillingCycleGatewayReportRow,
  nameFallback: string
): BillingMonthGatewayDisplayRow {
  return {
    gatewayName: formatGatewayName(row.gatewayName, nameFallback),
    distinctSubscribers: formatGatewayCount(row.distinctSubscribers),
    paid: formatGatewayCount(row.paid),
    refused: formatGatewayCount(row.refused),
    inFlight: formatGatewayCount(row.inFlight),
    approvalRatePercent: formatGatewayPercent(row.approvalRatePercent),
    sharePercent: formatGatewayPercent(row.sharePercent)
  };
}

function formatGatewayName(
  value: string | null | undefined,
  fallback: string
): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : fallback;
}
